/**
 * Download Utilities
 *
 * Functions for handling file downloads from S3 URLs
 */

/**
 * Downloads a file from a URL using a temporary anchor element
 *
 * Creates an invisible anchor, triggers click, then removes it.
 * Standard pattern for programmatic downloads.
 *
 * @param url - S3 download URL or file URL
 * @param filename - Desired filename for download
 *
 * @example
 * downloadFile('https://s3.../artifact.txt', 'results.txt');
 */
export const downloadFile = (url: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Downloads multiple files sequentially
 *
 * Browser limits concurrent downloads, so this spaces them out
 * with a delay to prevent download failures.
 *
 * @param downloads - Array of {url, filename} objects
 * @param delayMs - Delay between downloads (default: 500ms)
 */
export const downloadMultipleFiles = async (
  downloads: Array<{ url: string; filename: string }>,
  delayMs = 500
): Promise<void> => {
  for (const { url, filename } of downloads) {
    downloadFile(url, filename);
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }
};
